<template>
	<div class="w-100">
		<h3 class="chart-label m-4">{{ this.title }}</h3>
		<div id="marks-histogram" class="p-3"></div>
	</div>
</template>

<script>
import { select } from "d3-selection";
import { max } from "d3-array";
import { axisBottom, axisLeft } from "d3-axis";
import { scaleLinear } from "d3-scale";
import { easeBackInOut } from "d3-ease";
import "d3-transition";
import { getMarksDistribution } from "../../../utils/apiFunctions";

export default {
	name: "Histogram",
	props: [
		"url", // End of the API to get data
		"title", // Title of the graph
	],

	async mounted() {
		try {
			const response = await this.getData();
			this.barData = response.data;
			this.createChart();
		} catch (e) {
			console.error("Error getting data", e);
		}
	},

	data() {
		return {
			testId: this.$route.params.testID,
			barData: [],
		};
	},

	methods: {
		async getData() {
			const { data } = await getMarksDistribution(this.testId);
			if (data.type !== "Success") {
				throw new Error("Request Failed");
			} else {
				return data;
			}
		},

		createChart() {
			const parent = select("#marks-histogram");
			const margin = 20;
			const width = 810;
			const height = 360;

			//Getting the max values in order to cap the scale and adding one to counter ALL-ZERO problem
			const maxFrequency = max(this.barData, (data) => data.value) + 1;
			const xAxisTickValues = [
				"0",
				...this.barData.map((data) => data.name.split("-")[1]),
			];

			const svg = parent
				.append("svg")
				.attr(
					"viewBox",
					`${-margin} ${-margin} ${width + margin * 2} ${height + margin * 2}`
				);

			const rangeScale = scaleLinear()
				.domain([0, this.barData.length])
				.range([margin, width - margin * 2]);

			const frequencyScale = scaleLinear()
				.domain([0, maxFrequency])
				.range([height, 0]);

			const xAxis = axisBottom()
				.scale(rangeScale)
				.tickSize(7);
			const yAxis = axisLeft()
				.scale(frequencyScale)
				.ticks(maxFrequency);

			const rects = svg.append("g");
			const rectWidth = rangeScale(1) - rangeScale(0);
			svg
				.append("g")
				.attr("id", "x-axis")
				.call(xAxis)
				.attr("transform", `translate(0,${height})`)
				.selectAll("text")
				.data(xAxisTickValues)
				.text((data) => data);

			svg
				.append("g")
				.attr("id", "y-axis")
				.call(yAxis)
				.attr("transform", `translate(${margin},${0})`)
				.selectAll(".tick > line")
				.attr("stroke", "#9380F8")
				.attr("stroke-width", "2")
				.attr("stroke-dasharray", "3 4")
				.attr("x2", "0")
				.attr("x1", width)
				.attr("stroke", "#E3E3E3");

			// Removing axis lines
			svg.selectAll(".domain").attr("opacity", 0);
			//Changing axis colors
			svg
				.selectAll("text")
				.attr("fill", "#907DF7")
				.attr("font-size", 12);
			select("#x-axis")
				.selectAll("line")
				// .attr('y2', -height)
				.attr("stroke", "#907DF7");

			rects
				.selectAll("rect")
				.data(this.barData)
				.enter()
				.append("rect")
				.attr("x", (_, index) => rangeScale(index))
				.attr("y", frequencyScale(0))
				.attr("width", rectWidth)
				.attr("height", 0)
				.attr("fill-opacity", 0)
				.on("mouseenter", (e) => {
					e.target.style.opacity = 0.7;
				})
				.on("mouseleave", (e) => {
					e.target.style.opacity = 1;
				})
				.transition()
				.duration(2000)
				.ease(easeBackInOut)
				.attr("y", (data) => frequencyScale(data.value))
				.attr("height", (data) => frequencyScale(maxFrequency - data.value))
				.attr("fill", "#D7CFFE")
				.attr("fill-opacity", 0.8);
		},
	},
};
</script>

<style scoped>
.chart-label {
	font-size: var(--size-14);
	color: #72738e;
}
</style>
